import { Component, EventEmitter, Output } from '@angular/core';

export interface NewUserData {
  first_name: string;
  last_name: string;
  email: string;
}

export interface UserFormData {
  firstName: string;
  lastName: string;
  email: string;
  collapsed?: boolean;
}

@Component({
  selector: 'app-add-user-popup',
  templateUrl: './add-user-popup.component.html',
  styleUrls: ['./add-user-popup.component.css']
})
export class AddUserPopupComponent {
  headerTitle: string = "Add New Users";
  hasAttemptedSubmit: boolean = false;
  
  users: UserFormData[] = [
    {
      firstName: "",
      lastName: "",
      email: ""
      , collapsed: false
    }
  ];
  
  @Output() userAdded: EventEmitter<NewUserData[]> = new EventEmitter();
  @Output() cancelled: EventEmitter<void> = new EventEmitter();

  get isFormValid(): boolean {
    return this.users.every(user => 
      user.firstName.trim() !== '' && 
      user.email.trim() !== '' &&
      this.isValidEmail(user.email.trim())
    );
  }

  isValidEmail(email: string): boolean {
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailPattern.test(email);
  }

  get canAddUser(): boolean {
    // only allow adding a new user if all existing users are valid
    return this.users.every(u => this.isSingleUserValid(u));
  }

  tryAddUser() {
    if (this.canAddUser) {
      this.addUser();
      return;
    }

    // If not allowed to add, expand the invalid users so the user can fix them.
    this.users.forEach(u => {
      if (!this.isSingleUserValid(u)) {
        u.collapsed = false;
      }
    });
  }

  addUser() {
    // First validate all existing users. If any user is invalid, mark it and do not add a new one.
    const invalidIndices: number[] = [];
    this.users.forEach((u, idx) => {
      const valid = this.isSingleUserValid(u);
      if (!valid) {
        invalidIndices.push(idx);
        // ensure the invalid user is expanded so the user can fix it
        u.collapsed = false;
      }
    });

    if (invalidIndices.length > 0) {
      // At least one user is invalid — do not add a new user.
      return;
    }

    // auto-minimize the currently last user (if any) when adding a new one
    if (this.users.length > 0) {
      const lastIndex = this.users.length - 1;
      this.users[lastIndex].collapsed = true;
    }

    this.users.push({
      firstName: "",
      lastName: "",
      email: "",
      collapsed: false
    });
  }

  private isSingleUserValid(user: UserFormData): boolean {
    return user.firstName.trim() !== '' && user.email.trim() !== '' && this.isValidEmail(user.email.trim());
  }

  toggleMinimize(index: number) {
    if (this.users[index]) {
      this.users[index].collapsed = !this.users[index].collapsed;
    }
  }

  removeUser(index: number) {
    if (this.users.length > 1) {
      this.users.splice(index, 1);
    }
  }

  onSubmit() {
    this.hasAttemptedSubmit = true;
    if (this.isFormValid) {
      const userData: NewUserData[] = this.users.map(user => ({
        first_name: user.firstName.trim(),
        last_name: user.lastName.trim(),
        email: user.email.trim()
      }));
      this.userAdded.emit(userData);
    }
  }

  onCancel() {
    this.cancelled.emit();
  }

  close() {
    this.cancelled.emit();
  }
}
